import { Button, Divider, Group, Modal, Stack } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import useFreeSeatMutation from '../../queries/useFreeSeatMutation';

type Props = {
	opened: boolean;
	onClose: () => void;
	memberID: number;
	workspaceID: number;
	userEmail: string;
	projectsCount: number;
};

const FreeSeatModal = ( { opened, onClose, memberID, workspaceID, userEmail, projectsCount }: Props ) => {
	const { mutate: freeSeat, isLoading } = useFreeSeatMutation( { workspaceID, onSuccess: onClose } );

	const onConfirm = () => freeSeat( memberID );

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={(
				<Text
					variant="h5"
					style={{ color: 'var(--color-text-primary)', letterSpacing: '0.14px', lineHeight: '22px' }}
				>
					Free up seat
				</Text>
			)}
			size={440}
			withCloseButton={false}
			styles={{
				header: { paddingTop: 10, paddingBottom: 10, paddingLeft: 16, borderBottom: '1px solid var(--color-outline-default)' },
				body: { padding: 0 }
			}}
		>
			<Stack spacing={0}>
				<Stack spacing={15} px={24} pt={24} pb={24}>
					<Text
						variant="body"
						style={{ lineHeight: '22px', letterSpacing: '0.14px', color: 'var(--color-text-primary)' }}
					>
						{userEmail} will be moved to limited access across {projectsCount}{' '}
						{projectsCount === 1 ? 'project' : 'projects'}. Their seat
						will be available to reassign immediately.
					</Text>
					<Stack spacing={0}>
						{[
							'Their past contributions will remain intact.',
							'Ownership will be transferred to the workspace owner (you).'
						].map( item => (
							<Group key={item} align="flex-start" spacing={6} noWrap>
								<Text
									variant="body"
									style={{ lineHeight: '22px', color: 'var(--color-text-primary)', flexShrink: 0 }}
								>
									•
								</Text>
								<Text
									variant="body"
									style={{ lineHeight: '22px', letterSpacing: '0.14px', color: 'var(--color-text-primary)' }}
								>
									{item}
								</Text>
							</Group>
						) )}
					</Stack>
				</Stack>
				<Divider color="var(--color-outline-default)" />
				<Group position="right" spacing={12} px={12} py={6}>
					<Button
						variant="outline"
						onClick={onClose}
						disabled={isLoading}
						sx={{
							'color': 'var(--color-text-secondary)',
							'borderColor': 'var(--color-outline-default)',
							'fontSize': 14,
							'fontWeight': 600,
							'letterSpacing': 0,
							'lineHeight': '20px',
							'borderRadius': 4,
							'&:hover': { backgroundColor: 'var(--color-bg-surface-container)' }
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={onConfirm}
						loading={isLoading}
						color="blue.5"
						sx={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', letterSpacing: 0, borderRadius: 4 }}
					>
						Free up seat
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
};

export default FreeSeatModal;
